/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, March 5th 2022, 4:11:40 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { matchPath, useLocation } from "react-router-dom";
import { URLS, APP_URL_PATH_NAME_TYPE } from "./routes";
import { invariant } from "immutability-helper";

// From https://v5.reactrouter.com/web/example/query-parameters
export const useRouteQueryParams = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

// From https://mui.com/guides/routing/#tabs
export const useRouteMatch = (patterns) => {
  const { pathname } = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
};

export const compareURLPaths = (URL1: string, URL2: string) =>
  URL1?.replace(/^\/|\/$/g, "") === URL2?.replace(/^\/|\/$/g, "");

/**
 *
 * Function to resolve a URL Name. Inspired by Django's reverse shortcut / DTL url tag.
 * No such util found in react-router so did this.
 *
 * @param URLName The name of the URL that needs to be resolved. If a URL has path parameters you must provide either namedParamValues or unNamedParamValues. The length of parameters (whether named or unnamed) must be equal to the number of parameters in the URL string.
 * @param namedParamValues The path parameters in the URL string as an object where keys is the name of each paramter.
 * @param unNamedParamValues The path parameters of the URL string as a list. This will require listing of the parameters in the order with which they appear in url string.
 * @returns A resolved URL with supplied values as the path parameters.
 */
export const resolveURLName = (
  URLName: APP_URL_PATH_NAME_TYPE,
  namedParamValues?: { [key: string]: number | string },
  unNamedParamValues?: (number | string)[]
) => {
  const URLPath = URLS[URLName].endsWith("/") ? URLS[URLName] : `${URLS[URLName]}/`; // Appending a / in case it does not end with one because we need it in our regex

  const URLParams = URLPath.match(/:(.*?)(\/+)/g) ?? []; // Get the parameters defined in the url. This regex includes the colon (:) and backslash (/). The g flag indicates that the regular expression should be tested against all possible matches in a string - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/global

  const pathParameterCount = namedParamValues ? Object.keys(namedParamValues).length : unNamedParamValues?.length;

  invariant(
    typeof pathParameterCount !== "number" || URLParams.length === pathParameterCount,
    () =>
      `URL Resolution Error: The ${URLName} URL you are trying to resolve takes ${URLParams.length} parameters, you have provided ${pathParameterCount} parameter values`
  );

  const cleanedURLParams = URLParams.map((v) => v.replace("/", "")); // Removing /

  let cleanedURL: string;

  if (namedParamValues) {
    cleanedURL = getURLByNamedParams(cleanedURLParams, URLName, namedParamValues);
  } else {
    cleanedURL = getURLByUnNamedParams(cleanedURLParams, URLName, unNamedParamValues);
  }

  return cleanedURL.endsWith("/") ? cleanedURL : `${cleanedURL}/`;
};

const getURLByNamedParams = (
  cleanedURLParams: string[],
  URLName: string,
  namedParamValues: { [key: string]: string | number }
) => {
  return cleanedURLParams.reduce(
    (previous, current, index) => previous.replace(current, `${namedParamValues[current.substr(1)]}`), // substr(1) removes the :
    URLS[URLName]
  );
};

const getURLByUnNamedParams = (cleanedURLParams: string[], URLName: string, paramValues: (string | number)[]) => {
  return cleanedURLParams.reduce(
    (previous, current, index) => previous.replace(current, `${paramValues[index]}`),
    URLS[URLName]
  );
};

export const getAbsoluteURL = (path?: string) => `${window.location.protocol}//${window.location.host}${path}`;

export const getCurrentURL = () => getAbsoluteURL(window.location.pathname);

/**
 *
 * getPathWithoutTrailingParams: Note that this function only works with a url where the path parameter(s) trails
 * the whole URL e.g. /my-ur/:id. It will not work for URLs with path params in between e.g. /my-url/:id/abc/
 *
 * @param pathname
 * @returns
 */

export const getPathWithoutTrailingParams = (pathname: string) => {
  const cleaned_url = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return cleaned_url.match(/:(.*?)(\/+)/g).reduce((prev, curr) => prev.replace(curr, ""), cleaned_url);
};
